//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import LocationTrackerModel from '../models/location-tracker.model';

/**
 * @service LocationTrackerService
 * @description Service for managing location tracking data within the Trading Community module.
 * This service is responsible for retrieving location logs and visit tracking details.
 * It is primarily used to track the movement and visits of service providers (like technicians or sales representatives)
 * to various farm sites or business locations.
 *
 * The data retrieved by this service helps in:
 * 1. **Audit Trails**: Verifying that providers visited the locations they claimed to visit.
 * 2. **Geo-fencing**: Determining if actions were performed at the correct physical location.
 * 3. **Activity Monitoring**: Tracking the route and time spent by providers at different sites.
 *
 * This service extends the generic `Service` class to inherit standard CRUD capabilities and HTTP handling,
 * specifically targeting the `/tradingcommunity/locationtracker/` endpoint.
 *
 * @feature 1. Retrieve comprehensive lists of location tracker logs.
 * @feature 2. Fetch specific provider visit tracking details based on business hierarchy.
 * @feature 3. robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<LocationTrackerModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /tradingcommunity/locationtracker/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class LocationTrackerService extends Service<LocationTrackerModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/tradingcommunity/locationtracker/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/locationtracker/');
  }

  /**
   * @method getAllLocationTrackers
   * @async
   * @description Fetch all location tracker records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=20000) to ensure a comprehensive history is loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<LocationTrackerModel>>} Promise resolving to an array of LocationTrackerModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * locationTrackerService.getAllLocationTrackers().then(logs => {
   *   console.log('Location Logs:', logs);
   * });
   */
  public getAllLocationTrackers() {
    return new Promise<Array<LocationTrackerModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<LocationTrackerModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getProviderVisitTrackingDetailsByBusinessLevel
   * @async
   * @description Fetch provider visit tracking details.
   *
   * This endpoint is specific for retrieving location data structured around provider visits,
   * likely filtering based on the logged-in user's business context or hierarchy level on the backend.
   *
   * @returns {Promise<Array<LocationTrackerModel>>} Promise resolving to an array of LocationTrackerModel objects representing visits.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `getProviderVisitLocations`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getProviderVisitTrackingDetailsByBusinessLevel() {
    return new Promise<Array<LocationTrackerModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<LocationTrackerModel>>(`${this.baseUrl}${this.serviceUrl}getProviderVisitLocations`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
